import React, { type FC, memo, useCallback, useState } from "react";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import styles from "./Engine.module.scss";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import { getWLTPLabel } from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { useCarConfigSelector } from "../../../../../redux/store";
import { getEcoTagType } from "../../../../../../../shared-logic/utils/typedUtils";
import { ecoTagToLabel } from "../../../../../../../../common-deprecated/utils/ecoTagUtils";
import HybridLabel from "../../../../../../shared-components/css-modules/toyota/molecules/HybridLabel";
import { CarConfigLabel, getCarConfigLabel, useCarConfigOptionalLabel } from "../../../../../utils/labelUtils";
import type { MotorizationType } from "../../../../../../../shared-logic/types/Motorization";

const ITEMS_WHEN_COLLAPSED = 5;

const EngineDetails: FC<{ motorization: MotorizationType }> = ({ motorization }): JSX.Element => {
    const [showAll, setShowAll] = useState<boolean>(false);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carConfigSettings = useCarConfigSelector((state) => state.carConfigSettings);
    const wltpLabel = getWLTPLabel(
        commonSettings,
        motorization.engine.certification,
        motorization.engine.type.electric,
    );

    const onShowAllClick = useCallback(() => {
        setShowAll((oldShowAll) => !oldShowAll);
    }, []);

    const ecoTag = getEcoTagType({
        id: motorization.engine.id,
        name: motorization.engine.name,
        hybrid: motorization.engine.type.hybrid,
        electric: motorization.engine.type.electric,
        fuelCell: motorization.engine.type.fuelCell,
        plugin: motorization.engine.type.plugin,
        internalCode: motorization.engine.internalCode,
        type: motorization.engine.type.name,
        typeId: motorization.engine.type.id,
        typeCode: motorization.engine.type.internalCode,
    });

    const ecoTagLabel = ecoTagToLabel(ecoTag);
    const genericEngineDetailsDisclaimer = useCarConfigOptionalLabel("genericEngineDetailsDisclaimer");

    return (
        <div>
            <Text tag="h2" variant="apheleia-h3">
                {motorization.engine.name}
            </Text>
            <header className={styles.tableheader}>
                {ecoTagLabel && (
                    <HybridLabel
                        variant={ecoTag}
                        label={getCarConfigLabel({ commonSettings, carConfigSettings }, ecoTagLabel)}
                    />
                )}
                <Text variant="apheleia-body">
                    {motorization.engine.name}, {motorization.transmission.name}
                </Text>
            </header>
            <table className={styles.table}>
                <tbody>
                    {motorization.compareSpecs.slice(0, !showAll ? ITEMS_WHEN_COLLAPSED : undefined).map((spec) => (
                        <tr key={spec.id}>
                            <td>{spec.name}</td>
                            <td>
                                {spec.summary}
                                &nbsp;
                                {spec.unit}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {motorization.compareSpecs.length > ITEMS_WHEN_COLLAPSED && (
                <button type="button" onClick={onShowAllClick} className={styles["show-all"]}>
                    <CarConfigLabel label={showAll ? "carConfigEngineShowLess" : "carConfigEngineShowMore"} />
                    <span className={styles.icon}>
                        <Icon sizing="xs" variant={showAll ? "minus" : "plus"} aria-hidden="true" />
                    </span>
                </button>
            )}

            <div className={styles.disclaimers}>
                <Text
                    tag="span"
                    variant="apheleia-body"
                    color="grey-6"
                    className={styles.disclaimer}
                    dangerouslySetInnerHTML={{ __html: wltpLabel }}
                />

                {genericEngineDetailsDisclaimer && (
                    <Text tag="span" variant="apheleia-body" color="grey-6" className={styles.disclaimer}>
                        {genericEngineDetailsDisclaimer}
                    </Text>
                )}
            </div>
        </div>
    );
};

export default memo(EngineDetails);
